import React, { useEffect, useMemo } from "react";
import {
  Button,
  Card,
  Skeleton,
  Typography,
  InputLabel,
  TextField,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import useStyles from "./ControlGLReconciliation.styles";
import { useUserContext } from "../../../context/UserContext";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import { minDate, maxDate } from "../../../utils/utils";
import MonthRangeDatePickerWithPresets from "../../common/MonthRange/MonthRangeDatePickerWithPresets";
import { useSnowflakeContext } from "../../../context/SnowflakeContext";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import ControlGLReconciliationExport from "./ControlGLReconciliationExport";

const mapBusinessEntityID = (businessEntityID) => ({
  id: businessEntityID,
  name: businessEntityID,
});

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const flattenReportData = (
  nestedData,
  selectedFigureDivisor,
  selectedDecimalsIn,
) => {
  if (!nestedData || !Array.isArray(nestedData)) {
    return [];
  }

  const divisor = parseFloat(selectedFigureDivisor) || 1;
  const shouldDivide = divisor !== 1;

  const decimals =
    selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
      ? parseInt(selectedDecimalsIn, 10)
      : null;
  const shouldFormatDecimals = decimals !== null && !isNaN(decimals);

  return nestedData.map((row) => {
    const { ...fixedProps } = row;
    const formattedDatas = {};

    if (row) {
      for (const key in row) {
        if (Object.hasOwnProperty.call(row, key)) {
          let value = row[key];

          if (shouldDivide && !isNaN(value)) {
            value = value / divisor;
          }

          if (shouldFormatDecimals) {
            formattedDatas[key] = !isNaN(value)
              ? value?.toFixed(decimals)
              : value;
          } else {
            formattedDatas[key] = value;
          }
        }
      }
    }

    return {
      ...fixedProps,
      ...formattedDatas,
    };
  });
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const ControlGLReconciliationFiltersAndReports = () => {
  const { classes } = useStyles();
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const {
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    setSelectedPresetDateRange,
    selectedStartDate,
    setSelectedStartDate,
    selectedEndDate,
    setSelectedEndDate,

    globalControlHistoryEssbaseSummaryReconLoading,
    globalControlHistoryEssbaseSummaryReconError,
    globalControlHistoryEssbaseSummaryReconData,

    fetchGlobalControlHistoryEssbaseSummaryReconReport,

    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
    localCurrency,
    setLocalCurrency,
  } = useSnowflakeContext();

  const { regions } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const allsfcsRoles =
    (allsfcs || [])?.map((sfc) => sfc?.businessEntityID) || [];

  useEffect(() => {
    const { localCurrency: currency } =
      (allsfcs || [])?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
  }, [selectedBusinessEntityID, allsfcs, setLocalCurrency]);

  const mappedBusinessEntityIDs = [
    ...(allsfcsRoles || []).map(mapBusinessEntityID),
  ];

  const handleClick = () => {
    fetchGlobalControlHistoryEssbaseSummaryReconReport();
  };

  const columns = [
    {
      id: "BUSINESS_ENTITY_ID",
      label: "BUSINESS ENTITY ID",
      minWidth: 270,
      align: "left",
    },
    {
      id: "REPORT_DATE",
      label: "REPORT DATE",
      minWidth: 270,
      align: "left",
    },
    {
      id: "TFSCPARENT_NAME",
      label: "TFSCPARENT NAME",
      minWidth: 270,
      align: "left",
    },
    {
      id: "TRANSACTION_AMOUNT",
      label: "TRANSACTION AMOUNT",
      minWidth: 270,
      align: "left",
    },
    {
      id: "ESSBASE_AMOUNT",
      label: "ESSBASE AMOUNT",
      minWidth: 270,
      align: "left",
    },
    {
      id: "VARIANCE_AMOUNT",
      label: "VARIANCE AMOUNT",
      minWidth: 220,
      align: "left",
    },
  ];

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  const preprocessedData = flattenReportData(
    globalControlHistoryEssbaseSummaryReconData,
    selectedFigureDivisor,
    selectedDecimalsIn,
  );

  const preprocessedDataExport = flattenReportData(
    globalControlHistoryEssbaseSummaryReconData,
    null,
    null,
  );

  return (
    <Card
      style={{
        padding: "6px",
        marginBottom: "20px",
        width: "100%",
      }}
    >
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"report-date-range"}>Report Date Range</InputLabel>
          <MonthRangeDatePickerWithPresets
            text="Report Date Range"
            minStartDate={minDate}
            maxEndDate={maxDate}
            setSelectedPresetDateRange={setSelectedPresetDateRange}
            setSelectedStartDate={setSelectedStartDate}
            setSelectedEndDate={setSelectedEndDate}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            isLoading={selectedUserDetailLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleClick}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              !selectedBusinessEntityID ||
              !selectedStartDate ||
              !selectedEndDate
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              globalControlHistoryEssbaseSummaryReconLoading
            }
          />
        </Grid>
        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              globalControlHistoryEssbaseSummaryReconLoading
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.controlGLReconciliationExportBtn}>
            <ControlGLReconciliationExport
              columns={columns}
              datas={preprocessedDataExport}
              disabled={
                globalControlHistoryEssbaseSummaryReconLoading ||
                !globalControlHistoryEssbaseSummaryReconData?.length
              }
            />
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Typography>
            The below report shows the Global Control History Essbase Summary
            Recon Report for the selected filters. (Global View)
          </Typography>
          <br />
          {globalControlHistoryEssbaseSummaryReconLoading ? (
            <>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={100}
                animation="pulse"
                style={{ marginBottom: 8 }}
              />
              {Array.from({ length: 10 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width="100%"
                  height={26}
                  animation="pulse"
                  style={{ marginBottom: 8 }}
                />
              ))}
            </>
          ) : !globalControlHistoryEssbaseSummaryReconLoading &&
            globalControlHistoryEssbaseSummaryReconError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!globalControlHistoryEssbaseSummaryReconLoading &&
              !globalControlHistoryEssbaseSummaryReconData) ||
            (!globalControlHistoryEssbaseSummaryReconLoading &&
              !globalControlHistoryEssbaseSummaryReconData?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <Grid item xs={12} md={12} lg={12}>
              <ColumnGroupingTable
                columns={columns}
                datas={preprocessedData}
                loading={globalControlHistoryEssbaseSummaryReconLoading}
                tableHeight={400}
                freezeParentColumnCount={1}
                applyThousandsSeparator={true}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default ControlGLReconciliationFiltersAndReports;
